import { PropsWithChildren, CSSProperties } from 'react';
import { useTheme } from '@glu/theming';
import classnames from 'classnames';
import get from 'lodash/get';
import useStyles from './Box.styles';

export interface IBoxProps {
  /** Class name to be applied to the Box component */
  className?: string;
  /** The HTML tag to use as the wrapper. Default is 'div' */
  tag?: keyof JSX.IntrinsicElements;
  /** Used as a QA and automation hook. Default is 'glu-layout-box' */
  dataQa?: string;
  /**
   * An object with JSS style keys, and a value that is either valid JSS, or a string path to a theme value
   *
   * ```
   * const sx = {
   *   margin: 5,
   *   color: palette.primary.dark,
   *   backgroundColor: 'palette.background',
   * };
   * ```
   */
  sx?: CSSProperties & Record<string, string | number>;
}

/**
 * This component borrows from the Material UI Box concept:
 * https://github.com/mui/material-ui/blob/master/packages/mui-system/src/Box/Box.js
 */
export const Box = ({
  children,
  className,
  dataQa = 'glu-layout-box',
  sx = {},
  tag = 'div',
}: PropsWithChildren<IBoxProps>) => {
  const theme = useTheme();
  const parsedSx = Object.fromEntries(
    Object.entries(sx).map(([key, value]) => [key, get(theme, value, value)])
  );
  const classes = useStyles(parsedSx);
  const Tag = tag;

  return (
    <Tag className={classnames(className, classes.box)} data-qa={dataQa}>
      {children}
    </Tag>
  );
};

export default Box;
